<script setup lang="ts">
    const loginState = useLoginState();
    const { accountBasePath } = useAccountBasePath();
    const { t } = useI18n();
    const isAccountMenuOpen = ref(false);
    const loginModalController = useModal();
    const { data: story } = useConfig();
    const isLoggedIn = loginState.isKeycloakAuthenticated;
    const accountMenuAriaLabel = computed(() => {
        const accountLabel = t('account.menu.myAccount');
        const hello = t('account.menu.hello');
        return loginState.displayFirstName.value
            ? `${accountLabel}: ${hello}, ${loginState.displayFirstName.value}`
            : `${accountLabel}: ${hello}`;
    });

    async function invokeLogout() {
        isAccountMenuOpen.value = false;
        await loginState.logout();
    }

    const selfServiceUrl = useSelfServiceUrl();
    const selfServiceUrlAddresses = useSelfServiceUrl('addresses');

    const accountButtonLabel = computed(() => {
        return loginState.displayFirstName.value
            ? `${t('account.menu.hello')}, ${loginState.displayFirstName.value}!`
            : `${t('account.menu.hello')}!`;
    });
</script>

<template>
    <div
        v-if="!story?.content?.header?.[0]?.hideLogin"
        class="shopware-interface"
    >
        <div class="my-account-area">
            <ClientOnly>
                <div
                    v-show="!isLoggedIn"
                    class="button-wrapper"
                >
                    <ResponsiveSwitch>
                        <template #desktop>
                            <PrimaryButton
                                :aria-label="t('account.menu.signIn')"
                                :button-text="t('account.menu.signIn')"
                                native-button
                                has-icon
                                size="small"
                                data-testid="header-sign-in-link"
                                @click="loginModalController.open"
                            >
                                <template #customIcon>
                                    <Icon
                                        name="hfp:circle-user-round-white"
                                        size="24"
                                        alt=""
                                        aria-hidden="true"
                                        mode="svg"
                                    />
                                </template>
                            </PrimaryButton>
                        </template>
                        <template #mobile>
                            <SharedIconButton
                                :aria-label="t('account.menu.signIn')"
                                :button-text="t('account.menu.signIn')"
                                size="lg"
                                data-testid="header-sign-in-link"
                                icon="hfp:circle-user-round-white"
                                variant="black"
                                @click="loginModalController.open"
                            >
                            </SharedIconButton>
                        </template>
                    </ResponsiveSwitch>
                </div>
                <SharedLoginModal :login-modal-controller="loginModalController" />
                <div
                    v-if="isLoggedIn"
                    class="button-wrapper"
                >
                    <ResponsiveSwitch>
                        <template #desktop>
                            <PrimaryButton
                                :aria-label="accountMenuAriaLabel"
                                :button-text="accountButtonLabel"
                                native-button
                                size="small"
                                has-icon
                                data-testid="account-menu-hello-button"
                                @click="isAccountMenuOpen = !isAccountMenuOpen"
                            >
                                <template #customIcon>
                                    <Icon
                                        name="hfp:circle-user-round-white"
                                        size="24"
                                        alt=""
                                        aria-hidden="true"
                                    />
                                </template>
                            </PrimaryButton>
                        </template>
                        <template #mobile>
                            <SharedIconButton
                                :aria-label="accountMenuAriaLabel"
                                :button-text="accountButtonLabel"
                                size="lg"
                                data-testid="account-menu-hello-button"
                                icon="hfp:circle-user-round-white"
                                variant="black"
                                @click="isAccountMenuOpen = !isAccountMenuOpen"
                            >
                                <template #customIcon>
                                    <Icon
                                        name="hfp:circle-user-round-white"
                                        size="24"
                                        alt=""
                                        aria-hidden="true"
                                    />
                                </template>
                            </SharedIconButton>
                        </template>
                    </ResponsiveSwitch>
                    <BonusBadge />
                    <!-- Profile dropdown -->
                    <Transition>
                        <ul
                            v-if="isAccountMenuOpen"
                            :class="[isAccountMenuOpen ? 'block' : 'hidden']"
                            class="user-menu-container"
                            role="menu"
                            aria-orientation="vertical"
                            aria-labelledby="user-menu-button"
                            tabindex="-1"
                            @click="isAccountMenuOpen = false"
                        >
                            <li class="no-hover">
                                <h1>{{ t('account.menu.myAccount') }}</h1>
                            </li>
                            <li>
                                <NuxtLink
                                    id="user-menu-item-1"
                                    :to="accountBasePath"
                                    data-testid="header-my-account-link"
                                    tabindex="-1"
                                >
                                    <img
                                        class="icon"
                                        src="~/assets/icons/layout-dashboard.svg"
                                        alt=""
                                        aria-hidden="true"
                                    />
                                    {{ t('account.accountOverviewHeader') }}
                                </NuxtLink>
                            </li>
                            <li>
                                <NuxtLink
                                    id="user-menu-item-2"
                                    :to="selfServiceUrl"
                                    data-testid="header-my-account-link"
                                    tabindex="-2"
                                >
                                    <img
                                        class="icon"
                                        src="~/assets/icons/user-round.svg"
                                        alt=""
                                        aria-hidden="true"
                                    />
                                    {{ t('account.yourProfile') }}
                                </NuxtLink>
                            </li>
                            <li>
                                <NuxtLink
                                    id="user-menu-item-3"
                                    :to="selfServiceUrlAddresses"
                                    data-testid="header-my-account-link"
                                    tabindex="-3"
                                >
                                    <img
                                        class="icon"
                                        src="~/assets/icons/home.svg"
                                        alt=""
                                        aria-hidden="true"
                                    />
                                    {{ t('account.yourAddress') }}
                                </NuxtLink>
                            </li>
                            <li v-show="!story?.content?.header?.[0]?.hideCart">
                                <NuxtLink
                                    id="user-menu-item-4"
                                    :to="`${accountBasePath}/order`"
                                    data-testid="header-my-account-link"
                                    tabindex="-4"
                                >
                                    <img
                                        class="icon"
                                        src="~/assets/icons/book-user.svg"
                                        alt=""
                                        aria-hidden="true"
                                    />
                                    {{ t('account.orderHistoryHeader') }}
                                </NuxtLink>
                            </li>

                            <AccountMenuBonusItem />

                            <li>
                                <button
                                    id="user-menu-item-2"
                                    data-testid="header-sing-out-link"
                                    role="menuitem"
                                    tabindex="-3"
                                    @click="invokeLogout"
                                >
                                    <img
                                        class="icon"
                                        src="~/assets/icons/log-out.svg"
                                        alt=""
                                        aria-hidden="true"
                                    />
                                    {{ t('account.menu.signOut') }}
                                </button>
                            </li>
                        </ul>
                    </Transition>
                </div>
            </ClientOnly>
        </div>
    </div>
</template>

<style scoped>
    @import '~/assets/scss/navigation/account-menu.scss';
</style>
